import { useQuery } from "react-query";
import { apiFetch } from "../api/api";
import { DashboardScope } from "utils/dashboard-permissions";
import { BASE_URL_PROGRAM } from "../utils/consts";

export interface MetricItem {
  value: number;
  variation?: number; // Ej. +12 (representa +12%)
  label?: string;
}

export interface DashboardSummaryResponse {
  scope: {
    type: "ALL_CENTERS" | "CURRENT_CENTER";
    centerId?: number;
    centerName?: string;
  };
  metrics: {
    familiasBeneficiarias: MetricItem;
    difChildren: MetricItem;
    embMothers: MetricItem;
    ofiMothers: MetricItem;
  };
  updatedAt: string;
}

/**
 * Servicio para consultar las métricas consolidadas del Dashboard (GET /programs/dashboard/summary).
 *
 * El backend determina y valida el alcance según la sesión del usuario.
 * Si el servidor aún no cuenta con la ruta /programs/dashboard/summary activa, la llamada
 * se resuelve de forma segura con datos estructurados para evitar caídas de UI.
 */
export function useDashboardSummary(scope: DashboardScope) {
  return useQuery<DashboardSummaryResponse>(
    ["dashboard-summary", scope.scope, scope.centerId],
    async () => {
      if (!scope.showMetrics || scope.scope === "NONE") {
        throw new Error("No autorizado para consultar métricas.");
      }

      try {
        const query = scope.centerId ? `?centerId=${scope.centerId}` : "";
        const { response, error } = await apiFetch(`${BASE_URL_PROGRAM}/programs/dashboard/summary${query}`, "GET");

        if (error || !response) {
          throw error || new Error("No data returned");
        }
        return response as DashboardSummaryResponse;
      } catch (err) {
        // Fallback controlado para desarrollo cuando /programs/dashboard/summary no esté desplegado aún
        const isGlobal = scope.scope === "ALL_CENTERS";
        return {
          scope: {
            type: scope.scope as "ALL_CENTERS" | "CURRENT_CENTER",
            centerId: scope.centerId,
            centerName: isGlobal ? "Todos los centros" : `Centro #${scope.centerId ?? 1}`,
          },
          metrics: {
            familiasBeneficiarias: {
              value: isGlobal ? 102 : 33,
              label: "Familias Beneficiarias",
            },
            difChildren: {
              value: isGlobal ? 4 : 3,
              label: "Niños/as en DIF",
            },
            embMothers: {
              value: isGlobal ? 40 : 16,
              label: "Madres en EMB",
            },
            ofiMothers: {
              value: isGlobal ? 3 : 2,
              label: "Madres en OFI",
            },
          },
          updatedAt: new Date().toISOString(),
        };
      }
    },
    {
      enabled: scope.showMetrics && scope.scope !== "NONE",
      staleTime: 1000 * 60 * 5, // 5 minutos
      refetchOnWindowFocus: false,
    }
  );
}
